<div class="p-4">
    <jjames-card>
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-semibold">Detection Export</h2>
                <p class="text-gray-500 text-sm mt-1">Download bulk camera detection logs (CSV) with optional images
                    (ZIP).</p>
            </div>
            <mat-icon class="text-gray-400 text-4xl">download</mat-icon>
        </div>

        <!-- Camera Selector -->
        <div class="mb-6">
            <div class="flex items-center justify-between mb-2">
                <h3 class="text-sm font-semibold text-gray-700">Cameras</h3>
                <div class="flex gap-2">
                    <button mat-stroked-button color="primary" (click)="selectAll()" [disabled]="isLoadingCameras()">
                        Select All
                    </button>
                    <button mat-stroked-button (click)="deselectAll()" [disabled]="isLoadingCameras()">
                        Deselect All
                    </button>
                </div>
            </div>

            @if (isLoadingCameras()) {
            <div class="flex items-center gap-2 py-4 text-gray-500 text-sm">
                <mat-spinner diameter="20"></mat-spinner>
                Loading cameras…
            </div>
            }

            @if (!isLoadingCameras()) {
            <div
                class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-60 overflow-y-auto border rounded p-3 bg-gray-50">
                @for (cam of cameras(); track cam.id) {
                <label
                    class="flex items-center gap-2 px-3 py-2 rounded cursor-pointer hover:bg-white transition-colors border"
                    [class.bg-blue-50]="isCameraSelected(cam.id!)" [class.border-blue-300]="isCameraSelected(cam.id!)"
                    [class.bg-white]="!isCameraSelected(cam.id!)">
                    <mat-checkbox [checked]="isCameraSelected(cam.id!)" (change)="toggleCamera(cam.id!)"
                        color="primary"></mat-checkbox>
                    <div class="min-w-0">
                        <div class="text-sm font-medium truncate">{{ cam.deviceName || 'Unnamed Camera' }}</div>
                        <div class="text-xs text-gray-400 font-mono truncate">{{ cam.id }}</div>
                    </div>
                </label>
                }
                @if (cameras().length === 0) {
                <div class="col-span-full py-4 text-center text-gray-400 text-sm">
                    No cameras available.
                </div>
                }
            </div>
            }

            @if (selectedCameraIds().length > 0) {
            <div class="mt-1 text-xs text-blue-600">
                {{ selectedCameraIds().length }} camera(s) selected
            </div>
            }
        </div>

        <!-- Date Range -->
        <div class="mb-6 p-4 bg-gray-50 rounded border">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">Date Range <span class="text-gray-400 font-normal">(max
                    {{ MAX_DATE_RANGE_DAYS }} days)</span></h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="flex flex-col">
                    <label class="text-xs font-medium text-gray-700 mb-1">Start Date <span
                            class="text-red-500">*</span></label>
                    <input type="datetime-local" [ngModel]="startDate()"
                        (ngModelChange)="startDate.set($event); previewCount.set(null)"
                        class="px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                </div>
                <div class="flex flex-col">
                    <label class="text-xs font-medium text-gray-700 mb-1">End Date <span
                            class="text-red-500">*</span></label>
                    <input type="datetime-local" [ngModel]="endDate()"
                        (ngModelChange)="endDate.set($event); previewCount.set(null)"
                        class="px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                </div>
            </div>
            @if (dateRangeError()) {
            <div class="mt-2 flex items-center gap-1 text-red-600 text-xs">
                <mat-icon class="text-base">error_outline</mat-icon>
                {{ dateRangeError() }}
            </div>
            }
        </div>

        <!-- Optional Filters -->
        <div class="mb-6 p-4 bg-gray-50 rounded border">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">Optional Filters</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="flex flex-col">
                    <label class="text-xs font-medium text-gray-700 mb-1">Plate Number</label>
                    <input type="text" placeholder="e.g. ABC123" [ngModel]="plateNumber()"
                        (ngModelChange)="plateNumber.set($event); previewCount.set(null)"
                        class="px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                </div>
                <div class="flex flex-col">
                    <label class="text-xs font-medium text-gray-700 mb-1">Direction</label>
                    <select [ngModel]="direction()" (ngModelChange)="direction.set($event); previewCount.set(null)"
                        class="px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">All</option>
                        <option value="Approach">Approach</option>
                        <option value="Departure">Departure</option>
                        <option value="Both">Both</option>
                        <option value="Unknown">Unknown</option>
                    </select>
                </div>
            </div>
        </div>

        <!-- Include Images -->
        <div class="mb-6 flex items-center gap-3">
            <mat-checkbox [checked]="includeImages()" (change)="includeImages.set($event.checked)" color="primary">
                <span class="text-sm font-medium">Include images in download</span>
            </mat-checkbox>
            <span class="text-xs text-gray-400">
                {{ includeImages() ? 'Downloads as .zip (metadata.csv + images/)' : 'Downloads as .csv only (faster)' }}
            </span>
        </div>

        <!-- Actions -->
        <div class="flex flex-wrap items-center gap-3 mb-6">
            <jjames-button variant="secondary" [iconLeft]="'visibility'" (click)="preview()"
                [disabled]="isPreviewing() || isExporting()">
                @if (!isPreviewing()) {
                <span>Preview Count</span>
                }
                @if (isPreviewing()) {
                <span class="flex items-center gap-1">
                    <mat-spinner diameter="16"></mat-spinner>
                    Counting…
                </span>
                }
            </jjames-button>

            <jjames-button variant="primary" [iconLeft]="'download'" (click)="download()" [disabled]="downloadDisabled">
                Download {{ includeImages() ? 'ZIP' : 'CSV' }}
            </jjames-button>
        </div>

        <!-- Preview Result -->
        @if (previewCount() !== null && !isPreviewing()) {
        <div class="mb-6">
            <div class="flex items-center gap-2 px-4 py-3 rounded border text-sm"
                [class.bg-green-50]="previewCount()! <= MAX_RECORDS && previewCount()! > 0"
                [class.border-green-300]="previewCount()! <= MAX_RECORDS && previewCount()! > 0"
                [class.text-green-700]="previewCount()! <= MAX_RECORDS && previewCount()! > 0"
                [class.bg-red-50]="previewCount()! > MAX_RECORDS" [class.border-red-300]="previewCount()! > MAX_RECORDS"
                [class.text-red-700]="previewCount()! > MAX_RECORDS" [class.bg-yellow-50]="previewCount()! === 0"
                [class.border-yellow-300]="previewCount()! === 0" [class.text-yellow-700]="previewCount()! === 0">
                <mat-icon class="text-base">
                    {{ previewCount()! > MAX_RECORDS ? 'warning' : previewCount()! === 0 ? 'info' : 'check_circle' }}
                </mat-icon>
                @if (previewCount()! === 0) {
                <span>No records found for the selected filters.</span>
                }
                @if (previewCount()! > 0 && previewCount()! <= MAX_RECORDS) { <span>
                    <strong>{{ previewCount() | number }}</strong> records will be exported.
                    </span>
                    }
                    @if (previewCount()! > MAX_RECORDS) {
                    <span>
                        <strong>{{ previewCount() | number }}</strong> records found — exceeds the limit of <strong>{{
                            MAX_RECORDS | number }}</strong>.
                        Please narrow your date range or filters.
                    </span>
                    }
            </div>
        </div>
        }

        <!-- Progress Section -->
        @if (isExporting() || exportService.status() === 'done' || exportService.status() === 'error') {
        <div class="p-4 bg-gray-50 rounded border">
            <div class="flex items-center gap-2 mb-3">
                @if (isExporting()) {
                <mat-spinner diameter="20"></mat-spinner>
                }
                @if (exportService.status() === 'done') {
                <mat-icon class="text-green-600">check_circle</mat-icon>
                }
                @if (exportService.status() === 'error') {
                <mat-icon class="text-red-600">error</mat-icon>
                }
                <span class="text-sm font-medium" [class.text-green-700]="exportService.status() === 'done'"
                    [class.text-red-700]="exportService.status() === 'error'">
                    {{ statusMessage() }}
                </span>
            </div>

            @if (exportService.status() === 'downloading-images' && exportService.totalImages() > 0) {
            <mat-progress-bar mode="determinate" [value]="progressPercent()" class="rounded"></mat-progress-bar>
            <div class="mt-1 text-xs text-gray-500 text-right">{{ progressPercent() }}%</div>
            }

            @if (exportService.status() === 'fetching-metadata' || exportService.status() === 'building-zip') {
            <mat-progress-bar mode="indeterminate" class="rounded"></mat-progress-bar>
            }
        </div>
        }

    </jjames-card>
</div>